import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/common/Header';
import BottomNav from '../../components/common/BottomNav';
import { InlineLoader } from '../../components/common/Loader';
import { getConversations, deleteConversation } from '../../services/patientService';
import DeleteChatModal from '../../components/common/DeleteChatModal';
import { truncate } from '../../utils/helpers';

// ── Conversation card ──────────────────────────────────────────────────────
function ConvCard({ conv, isStore, onLongPress }) {
  const navigate   = useNavigate();
  const last       = conv.lastMessage;
  const pressTimer = useRef(null);
  const timeStr    = last?.createdAt
    ? new Date(last.createdAt).toLocaleTimeString('en',{hour:'2-digit',minute:'2-digit',hour12:true})
    : '';

  const preview = !last         ? 'Start chatting...'
    : last.type === 'image'     ? '📷 Photo'
    : last.type === 'pdf'       ? '📄 Document'
    : last.type === 'voice'     ? '🎙 Voice note'
    : truncate(last.message || '', 50);

  const handleClick = () => {
    if (isStore) {
      const match = conv._id.match(/store_([0-9a-f]{24})/i);
      const doctorId = match ? match[1] : null;
      if (doctorId) {
        navigate(`/medicine-chat/${doctorId}`, {
          state: { staffName: conv.storeName || 'Medicine Store', doctorName: conv.doctorName || '' }
        });
      }
    } else {
      // Chat is only for video consultations — clinic visits don't get doctor chat
      if (conv.appointmentType && conv.appointmentType !== 'video') {
        toast('💬 Chat is only available for Video Consultations.\nFor Clinic Visits, meet your doctor in person.', {
          duration: 4000,
          style: { fontSize: '13px', maxWidth: '320px' },
        });
        return;
      }
      navigate(`/chat/${conv._id}`, {
        state: {
          appointmentId: conv.appointmentId || null,
          doctorName:    conv.doctorName    || '',
          doctorImg:     conv.doctorImg     || null,
        }
      });
    }
  };

  // Long press to open delete menu
  const handlePressStart = () => {
    pressTimer.current = setTimeout(() => onLongPress(conv), 600);
  };
  const handlePressEnd = () => { clearTimeout(pressTimer.current); };

  return (
    <div className="relative group">
    <button onClick={handleClick}
      onMouseDown={handlePressStart} onMouseUp={handlePressEnd} onMouseLeave={handlePressEnd}
      onTouchStart={handlePressStart} onTouchEnd={handlePressEnd}
      className={`w-full flex items-center gap-3 px-4 py-3.5 border-b border-border active:bg-surface text-left transition-colors ${
        isStore ? 'bg-green-50/50' : 'bg-white'
      }`}>
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          isStore ? 'bg-green-100' : 'bg-primary-light'
        }`}>
          <i className={`fa-solid ${isStore ? 'fa-pills text-green-600' : 'fa-user-doctor text-primary'} text-lg`} />
        </div>
        {/* Lock badge for clinic appointments — chat not available */}
        {!isStore && conv.appointmentType && conv.appointmentType !== 'video' && (
          <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center shadow-sm">
            <i className="fa-solid fa-lock text-[8px]" />
          </span>
        )}
        {conv.unreadCount > 0 && conv.appointmentType === 'video' && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-sm font-bold text-text-primary truncate">
              {isStore ? (conv.storeName || 'Medicine Store') : (conv.doctorName || 'Doctor Chat')}
            </p>
            {isStore && (
              <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">
                STORE
              </span>
            )}
            {!isStore && conv.appointmentType === 'video' && (
              <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 flex items-center gap-0.5">
                <i className="fa-solid fa-video text-[8px]" /> VIDEO
              </span>
            )}
            {!isStore && conv.appointmentType === 'clinic' && (
              <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 flex items-center gap-0.5">
                <i className="fa-solid fa-lock text-[8px]" /> CLINIC
              </span>
            )}
          </div>
          <span className="text-[10px] text-text-muted flex-shrink-0">{timeStr}</span>
        </div>
        <p className={`text-xs truncate ${
          conv.unreadCount > 0 ? 'text-text-primary font-medium' : 'text-text-muted'
        }`}>
          {preview}
        </p>
      </div>

      <i className="fa-solid fa-chevron-right text-slate-300 text-xs flex-shrink-0" />
    </button>
    {/* Desktop hover delete button */}
    <button
      onClick={(e) => { e.stopPropagation(); onLongPress(conv); }}
      className="absolute right-10 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center justify-center w-8 h-8 bg-red-50 hover:bg-red-100 rounded-full transition-colors"
      title="Delete chat">
      <i className="fa-solid fa-trash-can text-danger text-xs" />
    </button>
    </div>
  );
}

// ── Section header ─────────────────────────────────────────────────────────
function SectionHeader({ icon, title, color, count }) {
  return (
    <div className={`flex items-center gap-2 px-4 py-2 border-b border-border ${color}`}>
      <i className={`fa-solid ${icon} text-sm`} />
      <span className="text-xs font-bold uppercase tracking-wider">{title}</span>
      {count > 0 && (
        <span className="ml-auto text-xs font-bold bg-white/60 px-2 py-0.5 rounded-full">
          {count}
        </span>
      )}
    </div>
  );
}

export default function ChatListPage() {
  const navigate                = useNavigate();
  const [convs, setConvs]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // conv to delete

  const loadConvs = () => {
    setLoading(true);
    getConversations()
      .then(res => {
        const docChats   = (res.conversations       || []);
        const storeChats = (res.storeConversations  || []);
        setConvs([...docChats, ...storeChats]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadConvs(); }, []); // eslint-disable-line

  const handleDelete = async (deleteFor) => {
    if (!deleteTarget) return;
    const targetId = deleteTarget._id;
    // Optimistically remove from UI immediately
    setConvs(prev => prev.filter(c => c._id !== targetId));
    setDeleteTarget(null);
    try {
      await deleteConversation(targetId, deleteFor);
      toast.success(deleteFor === 'both' ? 'Deleted for everyone' : 'Chat removed');
      // Reload to get fresh server state
      loadConvs();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
      // Restore on error
      loadConvs();
    }
  };

  const doctorChats = convs.filter(c => !c._id?.includes('store_'));
  const storeChats  = convs.filter(c =>  c._id?.includes('store_'));
  const totalUnread = convs.reduce((s, c) => s + (c.unreadCount || 0), 0);

  return (
    <div className="page-container">
      <Header title="Chats" showBack />

      {loading ? <InlineLoader text="Loading chats..." /> : convs.length === 0 ? (
        <div className="pb-20 px-4 pt-4 space-y-4">
          {/* Explain both chat types */}
          <p className="text-xs text-text-muted text-center pt-2 pb-1">Your chats will appear here once you start a conversation</p>

          {/* Doctor Chat card */}
          <div className="bg-primary-light border border-primary/20 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-stethoscope text-white text-sm" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-primary">Doctor Consultations</p>
              <p className="text-xs text-text-muted mt-0.5 leading-relaxed">
                Chat directly with your doctor after booking an appointment. Ask follow-up questions, share reports, and get advice.
              </p>
              <button
                onClick={() => navigate('/search')}
                className="mt-2 text-xs font-bold text-primary flex items-center gap-1">
                Find a Doctor <i className="fa-solid fa-arrow-right text-[10px]" />
              </button>
            </div>
          </div>

          {/* Medicine Store card */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-pills text-white text-sm" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-green-700">Medicine Store</p>
              <p className="text-xs text-text-muted mt-0.5 leading-relaxed">
                Chat with a doctor's linked pharmacy. Order medicines, ask about availability, and get prescription-based quotes.
              </p>
              <button
                onClick={() => navigate('/search')}
                className="mt-2 text-xs font-bold text-green-700 flex items-center gap-1">
                Browse Doctors <i className="fa-solid fa-arrow-right text-[10px]" />
              </button>
            </div>
          </div>

          <button
            onClick={() => navigate('/search')}
            className="w-full py-3 bg-primary text-white text-sm font-bold rounded-2xl flex items-center justify-center gap-2 mt-2">
            <i className="fa-solid fa-magnifying-glass" />
            Search Doctors to Start Chatting
          </button>
        </div>
      ) : (
        <div className="pb-20">

          {/* Doctor Chats */}
          {doctorChats.length > 0 && (
            <>
              <SectionHeader
                icon="fa-stethoscope"
                title="Doctor Consultations"
                color="bg-primary-light text-primary"
                count={doctorChats.reduce((s,c) => s + (c.unreadCount||0), 0)}
              />
              {doctorChats.map(c => <ConvCard key={c._id} conv={c} isStore={false} onLongPress={setDeleteTarget} />)}
            </>
          )}

          {/* Medicine Store Chats */}
          {storeChats.length > 0 && (
            <>
              <SectionHeader
                icon="fa-pills"
                title="Medicine Store"
                color="bg-green-50 text-green-700"
                count={storeChats.reduce((s,c) => s + (c.unreadCount||0), 0)}
              />
              {storeChats.map(c => <ConvCard key={c._id} conv={c} isStore={true} onLongPress={setDeleteTarget} />)}
            </>
          )}

        </div>
      )}

      <BottomNav notifCount={totalUnread} />

      {deleteTarget && (
        <DeleteChatModal
          onDeleteForMe={() => handleDelete('me')}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
